import {
  ButtonHTMLAttributes,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

import Typography from "../typography";
import { cn } from "../../utls/utils";

type InfoPopoverTone = "primary" | "neutral";
type InfoPopoverAlign = "start" | "center" | "end";

export interface InfoPopoverProps {
  title?: string;
  description?: ReactNode;
  children?: ReactNode;
  triggerLabel?: ReactNode;
  triggerIcon?: ReactNode;
  triggerAriaLabel?: string;
  tone?: InfoPopoverTone;
  align?: InfoPopoverAlign;
  className?: string;
  triggerProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

const toneStyles: Record<InfoPopoverTone, string> = {
  primary:
    "border-terracotta-200 bg-white text-brown-800 shadow-xl shadow-terracotta-100/70", 
  neutral:
    "border-beige-300 bg-cream-50 text-brown-700 shadow-lg shadow-brown-100/70",
};

const accentStyles: Record<InfoPopoverTone, string> = {
  primary: "bg-terracotta-500",
  neutral: "bg-brown-300",
};

const alignStyles: Record<InfoPopoverAlign, string> = {
  start: "left-0",
  center: "left-1/2 -translate-x-1/2",
  end: "right-0",
};

export function InfoPopover({
  title = "Need a quick refresher?",
  description,
  children,
  triggerLabel,
  triggerIcon,
  triggerAriaLabel = "Toggle info popover",
  tone = "primary",
  align = "center",
  className,
  triggerProps,
}: InfoPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const descriptionId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current?.contains(event.target as Node) ||
        triggerRef.current?.contains(event.target as Node)
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const { current } = panelRef;
    if (!current) return;

    const firstInteractiveElement = current.querySelector<HTMLElement>(
      "button, a, input, select, textarea"
    );

    firstInteractiveElement?.focus({ preventScroll: true });
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={cn("relative inline-flex", className)}>
      <button
        {...triggerProps}
        ref={triggerRef}
        type={triggerProps?.type ?? "button"}
        onClick={(event) => {
          triggerProps?.onClick?.(event);
          if (!event.defaultPrevented) {
            handleToggle();
          }
        }}
        className={cn(
          "inline-flex items-center gap-2 rounded-full border border-terracotta-200 bg-terracotta-50 px-3 py-1.5 text-sm font-medium text-terracotta-700",
          "transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-300 focus-visible:ring-offset-2",
          triggerProps?.className
        )}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={descriptionId}
        aria-label={
          triggerLabel ? undefined : triggerProps?.["aria-label"] ?? triggerAriaLabel
        }
      >
        <span className="h-4 w-4 text-terracotta-500" aria-hidden>
          {triggerIcon || <InformationCircleIcon />}
        </span>
        {triggerLabel ? <span>{triggerLabel}</span> : null}
      </button>

      {isOpen ? (
        <div
          ref={panelRef}
          role="dialog"
          id={descriptionId}
          aria-modal={false}
          className={cn(
            "absolute z-30 mt-3 w-72 rounded-xl border p-4", 
            "focus:outline-none", 
            alignStyles[align],
            toneStyles[tone]
          )}
        >
          <div
            aria-hidden
            className={cn(
              "absolute -top-1 left-1/2 h-2 w-10 -translate-x-1/2 rounded-full",
              accentStyles[tone]
            )}
          />
          <div className="space-y-2">
            {title ? (
              <Typography variant="subtitle2" className="text-terracotta-700">
                {title}
              </Typography>
            ) : null}
            <div className="text-sm leading-6">
              {children ? (
                children
              ) : description ? (
                <Typography variant="body2" className="text-brown-700">
                  {description}
                </Typography>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default InfoPopover;
